import { FunctionComponent, useCallback, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    SvgIcon,
    Tooltip,
    Typography,
} from '@mui/material';
import { TrashIcon } from '@heroicons/react/24/solid';
import { Dropzone } from '@starjack/shared-components';
import { VoucherFontDTO } from '@starjack/entities/voucherTemplate';

const ACCEPTED_FONTS = {
    'font/otf': ['.otf'],
    'font/ttf': ['.ttf'],
    'application/x-font-opentype': ['.otf'],
    'application/x-font-truetype': ['.ttf'],
};

type FontManagerDialogProps = {
    open: boolean;
    onClose: () => void;
    fonts: VoucherFontDTO[];
    isUploading: boolean;
    onUpload: (file: File) => Promise<boolean>;
    onDelete: (font: VoucherFontDTO) => Promise<boolean>;
};

export const FontManagerDialog: FunctionComponent<FontManagerDialogProps> = ({
    open,
    onClose,
    fonts,
    isUploading,
    onUpload,
    onDelete,
}) => {
    const [deleteConfirm, setDeleteConfirm] = useState<VoucherFontDTO | null>(
        null
    );

    const handleDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                await onUpload(acceptedFiles[0]);
            }
        },
        [onUpload]
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (deleteConfirm) {
            await onDelete(deleteConfirm);
            setDeleteConfirm(null);
        }
    }, [deleteConfirm, onDelete]);

    const systemFonts = fonts.filter((f) => !f.liftOperatorId);
    const customFonts = fonts.filter((f) => !!f.liftOperatorId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>Schriftarten verwalten</DialogTitle>
            <DialogContent>
                {/* Upload */}
                <Typography
                    variant='subtitle2'
                    sx={{ mb: 1 }}
                    color='text.secondary'
                >
                    SCHRIFTART HOCHLADEN
                </Typography>
                <Dropzone
                    onDrop={handleDrop}
                    accept={ACCEPTED_FONTS}
                    maxFiles={1}
                    disabled={isUploading}
                />
                <Typography variant='caption' color='text.secondary'>
                    Erlaubte Formate: .otf, .ttf
                </Typography>

                {/* Custom Fonts */}
                {customFonts.length > 0 && (
                    <>
                        <Typography
                            variant='subtitle2'
                            sx={{ mt: 3, mb: 1 }}
                            color='text.secondary'
                        >
                            EIGENE SCHRIFTARTEN ({customFonts.length})
                        </Typography>
                        <List dense>
                            {customFonts.map((font) => (
                                <ListItem
                                    key={font.id}
                                    secondaryAction={
                                        <Tooltip title='Löschen'>
                                            <IconButton
                                                size='small'
                                                onClick={() =>
                                                    setDeleteConfirm(font)
                                                }
                                            >
                                                <SvgIcon fontSize='small'>
                                                    <TrashIcon />
                                                </SvgIcon>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <ListItemText
                                        primary={font.name}
                                        secondary={`.${font.fileExtension}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {/* System Fonts */}
                {systemFonts.length > 0 && (
                    <>
                        <Typography
                            variant='subtitle2'
                            sx={{ mt: 3, mb: 1 }}
                            color='text.secondary'
                        >
                            SYSTEM-SCHRIFTARTEN ({systemFonts.length})
                        </Typography>
                        <List dense>
                            {systemFonts.map((font) => (
                                <ListItem key={font.id}>
                                    <ListItemText
                                        primary={font.name}
                                        secondary={`.${font.fileExtension}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {/* Delete Confirmation */}
                {deleteConfirm && (
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{
                            mt: 2,
                            p: 1.5,
                            backgroundColor: 'error.light',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant='body2' sx={{ flex: 1 }}>
                            &quot;{deleteConfirm.name}&quot; wirklich löschen?
                        </Typography>
                        <Button
                            size='small'
                            onClick={() => setDeleteConfirm(null)}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            size='small'
                            color='error'
                            variant='contained'
                            onClick={handleDeleteConfirm}
                        >
                            Löschen
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
